import type { CompareMode, PeriodGrain, PeriodPreset, PeriodSpec } from "@/analytics/core/types";
import { resolveDefaultCompare, resolvePeriodWindow } from "@/analytics/core/period-windows";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const PRESETS: PeriodPreset[] = [
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "this_year",
];

const GRAINS: PeriodGrain[] = ["week", "month", "quarter"];
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parsePreset(value: string | null): PeriodPreset {
  if (value && PRESETS.includes(value as PeriodPreset)) return value as PeriodPreset;
  return "this_month";
}

function parseGrain(value: string | null): PeriodGrain {
  if (value && GRAINS.includes(value as PeriodGrain)) return value as PeriodGrain;
  return "week";
}

function parsePeriodSpec(searchParams: URLSearchParams): PeriodSpec {
  const presetParam = searchParams.get("preset");
  if (presetParam === "custom") {
    const start = searchParams.get("start");
    if (start && ISO_DATE_RE.test(start)) {
      return {
        preset: "custom",
        grain: parseGrain(searchParams.get("grain")),
        customStart: start,
      };
    }
    return { preset: "this_month" };
  }
  return { preset: parsePreset(presetParam) };
}

function parseCompareMode(value: string | null): CompareMode {
  return value === "prev_year" ? "prev_year" : "prev_period";
}

function writePeriodSpec(next: URLSearchParams, spec: PeriodSpec): void {
  if (spec.preset === "custom" && spec.customStart) {
    next.set("preset", "custom");
    next.set("grain", spec.grain ?? "week");
    next.set("start", spec.customStart);
    return;
  }
  next.set("preset", spec.preset);
  next.delete("grain");
  next.delete("start");
}

function writeCompareMode(next: URLSearchParams, mode: CompareMode): void {
  if (mode === "prev_year") {
    next.set("compare", "prev_year");
  } else {
    next.delete("compare");
  }
}

export function useTeamDashboardParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const periodSpec = useMemo(() => parsePeriodSpec(searchParams), [searchParams]);
  const compareMode = useMemo(() => parseCompareMode(searchParams.get("compare")), [searchParams]);

  const period = useMemo(() => resolvePeriodWindow(periodSpec), [periodSpec]);
  const compare = useMemo(() => resolveDefaultCompare(period, compareMode), [period, compareMode]);

  const updateParams = useCallback(
    (patch: { periodSpec?: PeriodSpec; compareMode?: CompareMode }) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (patch.periodSpec) writePeriodSpec(next, patch.periodSpec);
        if (patch.compareMode) writeCompareMode(next, patch.compareMode);
        return next;
      });
    },
    [setSearchParams],
  );

  return { periodSpec, compareMode, period, compare, updateParams };
}
